import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MAPS_KEY } from "@/components/app/WorkplaceMap";
import SiteLayout from "@/components/site/SiteLayout";
import Home from "@/pages/site/Home";
import FeaturePage, { FeaturesIndex } from "@/pages/site/FeaturePage";
import Pricing from "@/pages/site/Pricing";
import { About, Contact } from "@/pages/site/AboutContact";
import Legal from "@/pages/site/Legal";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import { ForgotPassword, ResetPassword, VerifyEmail, AcceptInvite, GoogleCallback } from "@/pages/auth/AuthPages";
import AppLayout from "@/pages/app/AppLayout";
import Dashboard from "@/pages/app/Dashboard";
import Onboarding from "@/pages/app/Onboarding";
import { Workplaces, SettingsPage, Account, ComingSoon } from "@/pages/app/AppPages";
import { TimeClockPage, TimesheetsPage } from "@/pages/app/Ops";
import RosterPage from "@/pages/app/Roster";
import ReportsPage from "@/pages/app/Reports";
import ExpensesPage from "@/pages/app/Expenses";
import PayRunPage from "@/pages/app/PayRun";
import AdminPage from "@/pages/app/Admin";
import ProfilePage from "@/pages/app/Profile";
import EmployeeDirectory from "@/pages/app/EmployeeDirectory";
import EmployeeProfile from "@/pages/app/EmployeeProfile";
import BillingPage, { PaymentResult } from "@/pages/app/Billing";
import GpsPage from "@/pages/app/Gps";
import DocumentsPage from "@/pages/app/Documents";
import { AnnouncementsPage, LeavePage } from "@/pages/app/Comms";
import { FEATURES } from "@/data/features";

const Protected = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="grid min-h-screen place-items-center text-sm text-slate-500" data-testid="auth-loading">Loading…</div>;
  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
};

const RoleGate = ({ min, children }) => {
  const { membership } = useAuth();
  const rank = { employee: 1, manager: 2, owner: 3 };
  if (!membership) return null;
  return rank[membership.role] >= rank[min] ? children : <Navigate to="/app" replace />;
};

const FullAccess = ({ children }) => {
  const { membership } = useAuth();
  if (!membership) return null;
  return membership.role === "employee" && membership.app_access === "leave_only" ? <Navigate to="/app/leave" replace /> : children;
};

const AccountShell = () => {
  const { memberships, user } = useAuth();
  return memberships.length ? <Navigate to="/app/account-settings" replace /> : <div className="mx-auto max-w-3xl p-6">{user?.is_super_admin && <Link to="/admin" className="mb-4 inline-block text-sm font-semibold text-[#00A85C]" data-testid="account-back-admin">← Back to platform admin</Link>}<Account /></div>;
};

const TITLES = { "/privacy": "Privacy Policy", "/privacy-policy": "Privacy Policy", "/terms": "Terms and Conditions", "/terms-and-conditions": "Terms and Conditions", "/pricing": "Pricing", "/about": "About Us", "/contact": "Contact", "/login": "Log in", "/register": "Register your business", "/features": "Features" };

const PageTitle = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const feature = FEATURES[pathname.slice(1)]?.title;
    const page = TITLES[pathname] || feature || (pathname.startsWith("/app") ? "Dashboard" : null);
    document.title = page ? `${page} | Clockaroo` : "Clockaroo | Your Team. Your Time. All in One Place.";
  }, [pathname]);
  return null;
};

function AppRouter() {
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/features" element={<FeaturesIndex />} />
        {Object.keys(FEATURES).map((slug) => <Route key={slug} path={`/${slug}`} element={<FeaturePage slug={slug} />} />)}
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<Legal type="privacy" />} />
        <Route path="/terms" element={<Legal type="terms" />} />
        <Route path="/privacy-policy" element={<Legal type="privacy" />} />
        <Route path="/terms-and-conditions" element={<Legal type="terms" />} />
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
      <Route path="/app/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/app/account" element={<Protected><AccountShell /></Protected>} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="account-settings" element={<Account />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="time-clock" element={<FullAccess><TimeClockPage /></FullAccess>} />
        <Route path="live-attendance" element={<RoleGate min="manager"><TimeClockPage live /></RoleGate>} />
        <Route path="timesheets" element={<FullAccess><TimesheetsPage /></FullAccess>} />
        <Route path="rosters" element={<RosterPage />} />
        <Route path="announcements" element={<AnnouncementsPage />} />
        <Route path="leave" element={<LeavePage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="reports" element={<RoleGate min="manager"><ReportsPage /></RoleGate>} />
        <Route path="expenses" element={<RoleGate min="manager"><ExpensesPage /></RoleGate>} />
        <Route path="payroll" element={<RoleGate min="manager"><PayRunPage /></RoleGate>} />
        <Route path="gps-tracking" element={<RoleGate min="manager"><GpsPage /></RoleGate>} />
        <Route path="geofences" element={<RoleGate min="manager"><Workplaces /></RoleGate>} />
        <Route path="employees" element={<RoleGate min="manager"><EmployeeDirectory /></RoleGate>} />
        <Route path="employees/:mid" element={<RoleGate min="manager"><EmployeeProfile /></RoleGate>} />
        <Route path="settings" element={<RoleGate min="owner"><SettingsPage /></RoleGate>} />
        <Route path="subscription" element={<RoleGate min="owner"><BillingPage /></RoleGate>} />
        <Route path="*" element={<ComingSoon />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

const Maps = ({ children }) => (MAPS_KEY ? <APIProvider apiKey={MAPS_KEY} region="AU">{children}</APIProvider> : children);

export default function App() {
  return (
    <Maps>
      <BrowserRouter>
        <AuthProvider>
          <PageTitle />
          <AppRouter />
          <Toaster position="bottom-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </Maps>
  );
}
